import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { useAuth } from "./AuthContext";

const API = process.env.REACT_APP_BACKEND_URL;
const NotificationContext = createContext();
export const useNotifications = () => useContext(NotificationContext);

// Convert base64 URL string to Uint8Array for push subscription
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const rawData = window.atob(base64);
  return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
}

export function NotificationProvider({ children }) {
  const { user } = useAuth();
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushSupported, setPushSupported] = useState(false);
  const [pushEnabled, setPushEnabled] = useState(false);
  const prevUnreadRef = useRef(0);
  const audioRef = useRef(null);

  // Check if push is supported
  useEffect(() => {
    setPushSupported("serviceWorker" in navigator && "PushManager" in window);
  }, []);

  // Register service worker on mount
  useEffect(() => {
    if (!pushSupported) return;
    navigator.serviceWorker.register("/sw.js").then((reg) => {
      // Check if already subscribed
      reg.pushManager.getSubscription().then((sub) => {
        setPushEnabled(!!sub);
      });
    }).catch((err) => console.error("SW registration failed:", err));
  }, [pushSupported]);

  // Subscribe to push notifications
  const subscribePush = useCallback(async () => {
    if (!pushSupported) return false;
    try {
      const permission = await Notification.requestPermission();
      if (permission !== "granted") return false;

      const reg = await navigator.serviceWorker.ready;
      // Get VAPID public key from backend
      const { data } = await axios.get(`${API}/api/push/vapid-public-key`);
      if (!data.publicKey) {
        console.warn("VAPID public key not configured on server");
        return false;
      }

      const subscription = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(data.publicKey),
      });

      // Send subscription to backend
      await axios.post(`${API}/api/push/subscribe`, {
        endpoint: subscription.endpoint,
        keys: {
          p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey("p256dh")))),
          auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey("auth")))),
        },
      });

      setPushEnabled(true);
      return true;
    } catch (err) {
      console.error("Push subscribe failed:", err);
      return false;
    }
  }, [pushSupported]);

  // Unsubscribe from push
  const unsubscribePush = useCallback(async () => {
    try {
      const reg = await navigator.serviceWorker.ready;
      const subscription = await reg.pushManager.getSubscription();
      if (subscription) {
        await axios.post(`${API}/api/push/unsubscribe`, {
          endpoint: subscription.endpoint,
          keys: {},
        });
        await subscription.unsubscribe();
      }
      setPushEnabled(false);
    } catch (err) {
      console.error("Push unsubscribe failed:", err);
    }
  }, []);

  // Fetch unread count once
  const fetchUnread = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/inbox/unread-count`);
      const newCount = data.unread || 0;
      setUnreadCount((prev) => {
        if (newCount > prev && prev >= 0 && prevUnreadRef.current >= 0) {
          playNotificationSound();
        }
        prevUnreadRef.current = newCount;
        return newCount;
      });
    } catch {
      // ignore polling errors
    }
  }, []);

  // Poll unread count every 15 seconds
  useEffect(() => {
    if (!user) return;
    let active = true;

    const poll = async () => {
      if (!active) return;
      await fetchUnread();
    };

    poll(); // initial fetch
    const interval = setInterval(poll, 15000);
    return () => { active = false; clearInterval(interval); };
  }, [user, fetchUnread]);

  const playNotificationSound = () => {
    try {
      if (!audioRef.current) {
        audioRef.current = new Audio("/notification.wav");
        audioRef.current.volume = 0.5;
      }
      audioRef.current.currentTime = 0;
      audioRef.current.play().catch(() => {});
    } catch {
      // audio may be blocked by browser autoplay policy
    }
  };

  // Update document title with unread count
  useEffect(() => {
    if (unreadCount > 0) {
      document.title = `(${unreadCount}) Relay`;
    } else {
      document.title = "Relay";
    }
  }, [unreadCount]);

  return (
    <NotificationContext.Provider
      value={{
        unreadCount,
        setUnreadCount,
        refreshUnread: fetchUnread,
        pushSupported,
        pushEnabled,
        subscribePush,
        unsubscribePush,
      }}
    >
      {children}
    </NotificationContext.Provider>
  );
}
